// link-dialog.jsx
import React, { useState, useEffect, useRef } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import VariablePicker from "../../components/VariablePicker"
import usePanelManager from "../../hooks/usePanelManager.js"
import modalStack from "aldoo-ra/Modal/ModalStack"

const LinkDialog = ({
  defaultUrl = "",
  defaultIsDownload = false,
  defaultOpenInSameTab = false,
  variables = {},
  linkElement = null, // The actual link element for additional actions
  onClose,
}) => {
  const [url, setUrl] = useState(defaultUrl)
  const [isDownload, setIsDownload] = useState(defaultIsDownload)
  const [openInSameTab, setOpenInSameTab] = useState(defaultOpenInSameTab)
  const dialogRef = useRef(null)
  const variablesButtonRef = useRef(null)
  const [showVariablePicker, setShowVariablePicker] = useState(false)

  const variablePicker = usePanelManager('linkDialogVariablePicker')

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!url.trim()) return
    onClose({ url: url.trim(), isDownload, openInSameTab })
  }

  const handleVariablesClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setShowVariablePicker(!showVariablePicker)
  }

  const handleVariableSelect = (variable) => {
    setUrl(variable)
    setShowVariablePicker(false)
  }

  const handlePreview = () => {
    if (url.trim()) {
      window.open(url.trim(), "_blank")
    }
  }

  const handleDelete = () => {
    if (linkElement) {
      // Convert link to plain text
      const text = linkElement.textContent
      const textNode = document.createTextNode(text)
      linkElement.parentNode.replaceChild(textNode, linkElement)
    }
    onClose(null)
  }

  // Handle clicking outside dialog
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking inside the variable picker
      if (event.target.closest('[data-variable-picker="true"]')) {
        return
      }

      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose(null)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        if (showVariablePicker) {
          setShowVariablePicker(false)
        } else {
          onClose(null)
        }
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [onClose, showVariablePicker])

  // Handle clicking outside variable picker to close it
  useEffect(() => {
    if (!showVariablePicker) return

    const handleClickOutside = (event) => {
      // Don't close if clicking inside the variable picker
      if (event.target.closest('[data-variable-picker="true"]')) {
        return
      }

      // Don't close if clicking the Variables button
      if (variablesButtonRef.current && variablesButtonRef.current.contains(event.target)) {
        return
      }

      // Close the variable picker
      setShowVariablePicker(false)
    }

    // Add a small delay to prevent immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showVariablePicker])

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
        style={{ zIndex: (modalStack.getTop()?.zIndex ?? 0) + 10 }}
      >
        <div
          ref={dialogRef}
          className="bg-admin_paper rounded-lg shadow-xl w-full max-w-2xl mx-4"
        >
          {/* Dialog Header */}
          <div className="border-b border-admin_bordered flex items-center justify-between px-6 py-4">
            <Typography 
              variant="admin_h4"
              className="text-admin_text"
            >
              {defaultUrl ? "Edit Link" : "Add Link"}
            </Typography>

            <div ref={variablesButtonRef}>
              <Button
                variant="admin_secondary"
                onClick={handleVariablesClick}
                type="button"
                title="Insert Variable"
              >
                Variables
              </Button>
            </div>
          </div>

          {/* Dialog Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <Input
                variant="admin"
                label="URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://"
                autoFocus
                required
              />

              <div className="flex flex-row items-center justify-between gap-2">
                <Checkbox
                  isOn={isDownload}
                  onChange={setIsDownload}
                  label="This is a Download"
                  labelPosition="right"
                  variant="admin"
                />

                <Checkbox
                  isOn={openInSameTab}
                  onChange={setOpenInSameTab}
                  label="Open in Same Tab"
                  labelPosition="right"
                  variant="admin"
                />
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex justify-between items-center border-t border-admin_bordered px-6 py-4">
              {/* Left side - Preview and Delete buttons (only show for existing links) */}
              <div className="flex space-x-3">
                {linkElement && (
                  <>
                    <Button
                      variant="admin_secondary"
                      onClick={handlePreview}
                      type="button"
                      title="Open link in new tab"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Preview
                    </Button>
                    
                    <Button
                      variant="admin_error"
                      onClick={handleDelete}
                      type="button"
                      title="Remove link (keep text)"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </Button>
                  </>
                )}
              </div>

              {/* Right side - Cancel and Update/Add buttons */}
              <div className="flex space-x-3">
                <Button 
                  variant="admin_secondary" 
                  onClick={() => onClose(null)}
                >
                  Cancel
                </Button>

                <Button 
                  variant="admin_primary" 
                  onClick={handleSubmit}
                >
                  {defaultUrl ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>


      {/* Variable Picker Panel */}
      {showVariablePicker && (
        <div
          className="fixed"
          style={{
            left: variablesButtonRef.current?.getBoundingClientRect().left || 100,
            top: (variablesButtonRef.current?.getBoundingClientRect().bottom || 100) + 5,
            userSelect: 'none',
            zIndex: 9998
          }}
        >
          <VariablePicker
            variables={variables}
            onVariableSelect={handleVariableSelect}
            onClose={() => setShowVariablePicker(false)}
          />
        </div>
      )}
    </>
  )
}

export default LinkDialog
